import { Alert, Box, Chip, LinearProgress, Stack, Typography, alpha, useTheme } from "@mui/material";
import AutoAwesomeMotionOutlinedIcon from "@mui/icons-material/AutoAwesomeMotionOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import type { TrackedBatch } from "./types";

const terminal = new Set(["completed", "partial_failure", "failed", "cancelled", "blocked"]);

const originLabel = (batch: TrackedBatch): string => {
    switch (batch.handle?.origin) {
        case "product-hub-ui": return "Product Hub";
        case "feature-studio-ui": return "Feature Studio";
        default: return "FDE chat";
    }
};

const statusDetails = (batch: TrackedBatch) => {
    if (batch.phase === "completed") return { label: "Completed", color: "success" as const };
    if (["failed", "partial_failure", "blocked"].includes(batch.phase)) {
        return { label: batch.phase === "blocked" ? "Blocked" : "Needs attention", color: "error" as const };
    }
    if (batch.phase === "cancelled") return { label: "Cancelled", color: "default" as const };
    if (["checking", "ready", "queued"].includes(batch.phase)) return { label: "Queued", color: "default" as const };
    return { label: "In progress", color: "primary" as const };
};

export function BatchGenerationBackgroundRow({ batch }: { batch: TrackedBatch }) {
    const theme = useTheme();
    const status = statusDetails(batch);
    const failed = ["failed", "partial_failure", "blocked"].includes(batch.phase);
    const successful = batch.phase === "completed";
    const active = !terminal.has(batch.phase);
    const progress = batch.counts.total > 0
        ? Math.round(((batch.counts.completed + batch.counts.failed + batch.counts.cancelled) / batch.counts.total) * 100)
        : successful ? 100 : null;
    const gaps = batch.readiness?.products.filter(product => product.missingInputs.length > 0) || [];

    return (
        <Stack
            component="li"
            direction="row"
            spacing={1.5}
            sx={{
                listStyle: "none",
                px: 2,
                py: 1.6,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.72)}`
            }}
        >
            <Box
                sx={{
                    width: 34,
                    height: 34,
                    flex: "0 0 auto",
                    borderRadius: 1.5,
                    display: "grid",
                    placeItems: "center",
                    color: successful ? "success.main" : failed ? "error.main" : "primary.main",
                    bgcolor: successful
                        ? alpha(theme.palette.success.main, 0.1)
                        : failed
                          ? alpha(theme.palette.error.main, 0.1)
                          : alpha(theme.palette.primary.main, 0.09)
                }}
            >
                {successful ? <CheckCircleOutlineIcon sx={{ fontSize: 19 }} /> : failed ? <ErrorOutlineIcon sx={{ fontSize: 19 }} /> : <AutoAwesomeMotionOutlinedIcon sx={{ fontSize: 18 }} />}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, lineHeight: 1.35, fontWeight: 700 }}>
                            Generate All Work Products
                        </Typography>
                        <Typography sx={{ mt: 0.15, fontSize: 11.25, color: "text.secondary" }}>
                            Batch · Started from {originLabel(batch)}
                        </Typography>
                    </Box>
                    <Chip
                        label={status.label}
                        color={status.color}
                        size="small"
                        variant={status.color === "default" ? "outlined" : "filled"}
                        sx={{ height: 22, fontSize: 10.5, fontWeight: 700, "& .MuiChip-label": { px: 0.9 } }}
                    />
                </Stack>

                <Typography sx={{ mt: 0.65, fontSize: 11.5, lineHeight: 1.4, color: failed ? "error.main" : "text.secondary" }}>
                    {batch.message}
                </Typography>

                {batch.counts.total > 0 && (
                    <Typography sx={{ mt: 0.45, fontSize: 10.75, lineHeight: 1.4, color: "text.secondary" }}>
                        {batch.counts.pending} pending · {batch.counts.queued} queued · {batch.counts.running} running · {batch.counts.completed} completed · {batch.counts.failed} failed{batch.counts.cancelled > 0 ? ` · ${batch.counts.cancelled} cancelled` : ""}
                    </Typography>
                )}

                {active && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                        <LinearProgress
                            variant={progress === null ? "indeterminate" : "determinate"}
                            value={progress ?? undefined}
                            aria-label="Generate All Work Products progress"
                            sx={{ flex: 1, height: 4, borderRadius: 4 }}
                        />
                        {progress !== null && (
                            <Typography sx={{ minWidth: 30, fontSize: 10.5, fontWeight: 700, color: "text.secondary", textAlign: "right" }}>
                                {progress}%
                            </Typography>
                        )}
                    </Stack>
                )}

                {gaps.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 1, py: 0.25, "& .MuiAlert-message": { width: "100%" } }}>
                        {gaps.map(product => (
                            <Box key={product.productId} sx={{ mt: 0.5 }}>
                                <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>{product.productName}</Typography>
                                {product.missingInputs.map(input => <Typography key={input} sx={{ fontSize: 11 }}>• {input}</Typography>)}
                            </Box>
                        ))}
                    </Alert>
                )}

                {batch.handle?.warnings.map(warning => (
                    <Typography key={warning} sx={{ mt: 0.5, fontSize: 10.75, color: "warning.main" }}>
                        {warning}
                    </Typography>
                ))}
            </Box>
        </Stack>
    );
}
